/**
 * Why a barcode scan produced nothing, in a shape PostHog can answer questions from (ATL-1262).
 *
 * WHY THIS EXISTS
 * ---------------
 * A scan that resolves nothing is SILENT: `findProductByBarcode` logs to the console and the
 * screen does not change. Diagnosing one used to mean asking the merchant what their scanner
 * does, and the three causes are indistinguishable from the outside:
 *
 *   - the scanner sends numpad key codes, which the pre-ATL-1261 build read as letters;
 *   - the code reached the lookup and is genuinely not in this organization's catalog.
 *
 * A third cause, a scanner with no Enter suffix, was here until ATL-1263 completed such a burst
 * on its pause instead. It survives as the `terminated_by_enter` field rather than a reason.
 *
 * Each leaves a different fingerprint in the keystrokes themselves, so the event below carries
 * the fields that SEPARATE them rather than a general dump. `reason` is the first one that
 * applies, in the order a scan actually fails.
 *
 * WHAT IS DELIBERATELY NOT SENT
 * -----------------------------
 * The New Sale screen listens on `document` keydown, so the buffer sees every keystroke made
 * while that screen is open — not only scans. Sending it verbatim would ship whatever a cashier
 * typed to an analytics vendor. `code` is therefore emitted ONLY when the whole buffer is
 * digits, which is what a barcode is and what a typed sentence is not; anything else reports
 * its shape (`code_length`, `code_charset`) and sets `code_redacted`. That is not a formality:
 * `not_in_catalog` is the one reason whose remedy needs the digits, and it is also the only
 * reason whose buffer is reliably numeric.
 */

/** Does the whole buffer look like a barcode rather than something a person typed? */
const isBarcodeShaped = (code) => code.length > 0 && /^[0-9]+$/.test(code)

/** Coarse charset label for a buffer we refuse to send verbatim. */
const charsetOf = (code) => {
  if (code.length === 0) return 'empty'
  if (/^[0-9]+$/.test(code)) return 'digits'
  if (/^[0-9A-Za-z]+$/.test(code)) return 'alnum'
  return 'mixed'
}

/**
 * Build the `barcode_scan_failed` payload, or `null` when the scan actually resolved a product
 * and there is nothing to report.
 *
 * @param {object} args
 * @param {Array<{key: string, code: string, t: number}>} args.records  keystrokes of this burst
 * @param {string} args.code            the buffer as the lookup received it (post-truncation)
 * @param {boolean} args.terminatedByEnter  did an Enter close this burst
 * @param {boolean} args.found          did `findProductByBarcode` resolve a product
 * @param {number} args.catalogSize     barcodes indexed for this organization
 * @returns {object|null}
 */
export const describeFailedScan = ({ records, code, terminatedByEnter, found, catalogSize }) => {
  if (found) return null

  const chars = records.filter((r) => typeof r.key === 'string' && r.key.length === 1)
  // Nothing was typed at all — a bare Enter confirming a dialog is not a failed scan, and
  // reporting it would bury the real ones under keyboard noise.
  if (chars.length === 0) return null

  const gaps = []
  for (let i = 1; i < chars.length; i++) gaps.push(Math.round(chars[i].t - chars[i - 1].t))

  const hasNumpadKeys = records.some((r) => typeof r.code === 'string' && r.code.indexOf('Numpad') === 0)

  // First cause that applies, in the order a scan fails: the code must be readable, then it
  // must exist.
  //
  // `no_enter_suffix` was a reason here until ATL-1263 and is deliberately no longer one. A
  // burst that no Enter closed now completes on the scanner's own pause, so a missing suffix
  // stopped being a failure at all — it is reported as the `terminated_by_enter` FIELD, which
  // still says which scanners lack one, without pretending their scans failed.
  let reason
  if (hasNumpadKeys) reason = 'numpad_keys'
  else reason = 'not_in_catalog'

  const barcodeShaped = isBarcodeShaped(code)

  return {
    reason,
    code: barcodeShaped ? code : null,
    code_redacted: !barcodeShaped,
    code_length: code.length,
    code_charset: charsetOf(code),
    char_count: chars.length,
    // Stray non-character keys only. Enter is excluded deliberately: it terminates every
    // successful burst, so counting it would put a floor of 1 under a field whose whole job
    // is to say how much keyboard noise landed in the buffer.
    dropped_key_count: records.length - chars.length - (terminatedByEnter ? 1 : 0),
    terminated_by_enter: terminatedByEnter,
    has_numpad_keys: hasNumpadKeys,
    max_gap_ms: gaps.length ? Math.max(...gaps) : null,
    catalog_size: catalogSize,
  }
}
